import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Partner } from "@meupedaco/shared";
import { CrudPage } from "../../components/CrudPage";
import { useAuth } from "../../auth/AuthContext";
import { TIPOS_SOCIO, TIPOS_SOCIO_CONTRATO } from "../../lib/options";
import { formatNumberBR } from "../../utils/format";
import { parseMoneyBR } from "../../utils/parseMoneyBr";

export const PartnersPage: React.FC = () => {
  const { client } = useAuth();
  const [roleOptions, setRoleOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  useEffect(() => {
    let active = true;
    const loadRoles = async () => {
      try {
        const response = await client.get(`/modules/funcoes?take=100&skip=0`);
        if (!response.ok) {
          throw new Error("Falha ao carregar funcoes");
        }
        const payload = (await response.json()) as {
          data: Array<{ id: number; name: string }>;
        };
        if (!active) {
          return;
        }
        setRoleOptions(
          payload.data.map((item) => ({
            value: String(item.id),
            label: item.name,
          }))
        );
      } catch (error) {
        console.error(error);
      }
    };
    loadRoles();
    return () => {
      active = false;
    };
  }, [client]);

  const formatPercent = (value: unknown) =>
    `${formatNumberBR(parseMoneyBR(value) ?? 0)}%`;

  const roleField = useMemo(() => {
    if (roleOptions.length > 0) {
      return {
        name: "roleId",
        label: "Funcao",
        type: "select" as const,
        options: roleOptions,
      };
    }
    return {
      name: "roleId",
      label: "Funcao (ID)",
      type: "number" as const,
      placeholder: "Opcional",
    };
  }, [roleOptions]);

  return (
    <CrudPage<Partner>
      title="Socios"
      endpoint="/modules/partners"
      searchPlaceholder="Buscar por nome..."
      columns={[
        { label: "Nome", render: (item) => item.entity?.name ?? item.name },
        { label: "Documento", render: (item) => item.entity?.document ?? item.document ?? "-" },
        {
          label: "Participacao (%)",
          render: (item) => formatPercent(item.participationPercent ?? 0),
        },
        {
          label: "Contribuicao (%)",
          render: (item) => formatPercent(item.financialContributionPct ?? 0),
        },
        {
          label: "Tipo",
          render: (item) =>
            TIPOS_SOCIO_CONTRATO.find((option) => option.value === item.partnerType)
              ?.label ?? item.partnerType ?? "-",
        },
        {
          label: "Perfil",
          render: (item) =>
            TIPOS_SOCIO.find((option) => option.value === item.partnerKind)?.label ??
            item.partnerKind ??
            "-",
        },
        {
          label: "Pro-labore",
          render: (item) => (item.receivesProLabore ? "Sim" : "Nao"),
        },
        { label: "Funcao", render: (item) => item.roleRef?.name ?? "-" },
        { label: "Ativo", render: (item) => (item.isActive ? "Sim" : "Nao") },
        {
          label: "Transacoes",
          render: (item) => (
            <Link className="btn btn-sm btn-outline-primary" to={`/pessoas/socios/${item.id}`}>
              Ver
            </Link>
          ),
        },
      ]}
      formFields={[
        roleField,
        {
          name: "ownershipPct",
          label: "Ownership (%)",
          type: "text",
          displayValue: (value) => formatPercent(value ?? 0),
        },
        {
          name: "participationPercent",
          label: "Participacao (%)",
          type: "text",
          displayValue: (value) => formatPercent(value ?? 0),
        },
        {
          name: "financialContributionPct",
          label: "Contribuicao financeira (%)",
          type: "text",
          displayValue: (value) => formatPercent(value ?? 0),
        },
        { name: "partnerFunction", label: "Funcao interna", type: "text" },
        {
          name: "partnerKind",
          label: "Tipo de socio",
          type: "select",
          options: TIPOS_SOCIO,
        },
        {
          name: "partnerType",
          label: "Tipo",
          type: "select",
          options: TIPOS_SOCIO_CONTRATO,
        },
        { name: "receivesProLabore", label: "Recebe pro labore", type: "checkbox" },
        { name: "isActive", label: "Ativo", type: "checkbox" },
      ]}
      initialValues={{
        roleId: null,
        ownershipPct: "",
        participationPercent: "",
        financialContributionPct: "",
        partnerFunction: "",
        partnerKind: "INVESTIDOR",
        partnerType: "ACTIVE",
        receivesProLabore: false,
        isActive: true,
      }}
      mapToPayload={(values) => {
        const parsePercentValue = (value: unknown) => {
          if (value === null || value === undefined || value === "") {
            return null;
          }
          return parseMoneyBR(value);
        };

        return {
          roleId: values.roleId ? Number(values.roleId) : null,
          ownershipPct: parsePercentValue(values.ownershipPct),
          participationPercent: parsePercentValue(values.participationPercent),
          financialContributionPct: parsePercentValue(values.financialContributionPct),
          partnerFunction: values.partnerFunction || null,
          partnerKind: values.partnerKind || "INVESTIDOR",
          partnerType: values.partnerType || "ACTIVE",
          receivesProLabore: values.receivesProLabore,
          isActive: values.isActive,
        };
      }}
      permissionWrite="FINANCE_WRITE"
    />
  );
};
